/** Original authored arcade gait model. No source/assets copied from Clip-Clop. */
export const FIXED_STEP = 1 / 120;
export const COURSE_LENGTH = 50;
export const TIME_LIMIT = 90;
export const BALANCE_LIMITS = { roll: .58, pitch: .56 } as const;
export const LEGS = [
  { name: '왼 앞', key: 'KeyI', x: .72, z: -.29, color: '#de657e' },
  { name: '오른 앞', key: 'KeyJ', x: .72, z: .29, color: '#438fca' },
  { name: '왼 뒤', key: 'KeyR', x: -.65, z: -.29, color: '#e2a638' },
  { name: '오른 뒤', key: 'KeyF', x: -.65, z: .29, color: '#62a280' },
] as const;
// A coordinated lift needs enough travel to be a stroke, not a key chatter.
export const STROKE_TIMING = { minLift: .05, maxLift: .26, chordGrace: .04, maxGap: .36 } as const;
const DIAGONALS = [0b1001, 0b0110] as const;
export type Phase = 'ready' | 'running' | 'fallen' | 'won' | 'lost';
export const STANCE_REACH = -.48;
export interface Leg { reach: number; lift: number; contact: boolean; plantX: number; steps: number }
export class Gait {
  phase: Phase = 'ready';
  x = 0; speed = 0; time = 0; falls = 0; steps = 0;
  roll = 0; pitch = 0; rollSpeed = 0; pitchSpeed = 0;
  height = 1.26; airTime = 0; fallAge = 0;
  reason = ''; lastStride = -1; started = false;
  legs: Leg[] = LEGS.map(l => ({ reach: -.10, lift: 0, contact: true, plantX: l.x - .10, steps: 0 }));
  private strokePair = 0;
  private strokeAge = 0;
  private coherentLift = 0;
  private chordAge = 0;
  private previousPair = 0;
  private previousRelease = -Infinity;
  private drivingPair = 0;
  private exhaustedPair = 0;
  get balanceRisk() {
    // Warn while there is still time to put a foot down, including a fast
    // outward lean that has not yet reached the visible angle threshold.
    return Math.max(Math.abs(this.roll) / BALANCE_LIMITS.roll,
      Math.abs(this.pitch) / BALANCE_LIMITS.pitch,
      Math.abs(this.roll + this.rollSpeed * .12) / BALANCE_LIMITS.roll,
      Math.abs(this.pitch + this.pitchSpeed * .12) / BALANCE_LIMITS.pitch);
  }
  update(dt: number, input: readonly boolean[]) {
    if (this.phase === 'won' || this.phase === 'lost') return;
    if (!this.started && input.some(Boolean)) { this.started = true; this.phase = 'running'; }
    if (!this.started) return;
    this.time += dt;
    if (this.time >= TIME_LIMIT) { this.time = TIME_LIMIT; this.phase = 'lost'; this.speed = 0; return; }
    if (this.phase === 'fallen') {
      this.fallAge += dt; this.speed *= Math.exp(-dt * 6);
      // Input still animates each leg, without propulsion, landing score or auto-recovery.
      for (let i = 0; i < 4; i++) {
        this.moveLeg(this.legs[i], Boolean(input[i]), dt);
        this.legs[i].contact = false;
      }
      return;
    }
    this.coordinateStroke(dt, input);
    let power = 0, supports = 0, lateral = 0, supportX = 0, diagonal = 0;
    let totalGrip = 0, overLift = 0;
    for (let i = 0; i < 4; i++) {
      const leg = this.legs[i], def = LEGS[i], held = Boolean(input[i]);
      const oldReach = leg.reach, oldLift = leg.lift;
      this.moveLeg(leg, held, dt);
      // Swinging mass rocks the body immediately. A skewed chord therefore
      // leaves angular momentum even after the second key catches up.
      const swing = leg.lift - oldLift;
      this.rollSpeed += Math.sign(def.z) * swing * .48;
      this.pitchSpeed -= swing * .28;
      // Short lifts regain purchase early on release; long lifts spend more
      // of their finite return travel airborne before they can push.
      const grip = Math.max(0, Math.min(1, (.68 - leg.lift) * 3.5));
      // Forward swing is not ground support, even before filtered lift rises.
      const contact = !held && grip > .05;
      totalGrip += contact ? grip : 0;
      overLift += Math.max(0, leg.lift - .72);
      if (contact && !leg.contact) {
        // Higher, later landings hit harder. Opposite hooves can catch a lean,
        // but an uneven touchdown can also reinforce it. Never reset momentum.
        const impact = oldLift * (.32 + this.speed * .045);
        this.rollSpeed -= Math.sign(def.z) * impact;
        this.pitchSpeed += Math.sign(def.x) * impact * .65;
        leg.plantX = this.x + def.x + leg.reach;
        leg.steps++; this.steps++; this.lastStride = i;
      }
      leg.contact = contact;

      if (contact) {
        supports++; lateral += Math.sign(def.z) * grip;
        // Grounded hoof location, including how far the body has travelled past it.
        supportX += (leg.plantX - this.x) * grip;
        diagonal += Math.sign(def.x * def.z);
        // Only an actual backward stance stroke supplies propulsion. Holding or idle cannot run.
        if (this.drivingPair & (1 << i)) {
          const remainingStance = Math.max(0, leg.plantX - this.x - def.x - STANCE_REACH);
          const work = Math.min(Math.max(0, oldReach - leg.reach), remainingStance);
          power += work / dt * grip * grip * grip;
        }
      } else leg.plantX = this.x + def.x + leg.reach;
    }
    // Strong ground drag: motion comes from fresh pushes, not long coasting.
    const acceleration = power * 8 - 12 * this.speed - .15 * this.speed ** 2;
    this.speed = Math.max(0, Math.min(5.6, this.speed + acceleration * dt));
    // Ground reaction stops the body at full stance extension. Never drag a
    // planted anchor or invent an automatic step to accommodate root motion.
    let travel = this.speed * dt;
    for (let i = 0; i < 4; i++) if (this.legs[i].contact) {
      travel = Math.min(travel, Math.max(0, this.legs[i].plantX - this.x - LEGS[i].x - STANCE_REACH));
    }
    this.speed = travel / dt;
    this.x += travel;
    // A diagonal is a narrow support line, not the broad base of four feet.
    // It steadies fore/aft motion but lets lateral momentum build; changing
    // diagonals changes the tipping axis. Only real grounded grip can catch it.
    const diagonalSupport = supports === 2 && Math.abs(diagonal) === 2;
    const broadSupport = Math.max(0, Math.min(1, (totalGrip - 2) / 1.2));
    const rollRestore = 2 + broadSupport * 24 + (diagonalSupport ? totalGrip * 4 : 0);
    const pitchRestore = 2 + broadSupport * 22 + (diagonalSupport ? totalGrip * 3 : 0);
    const damping = 1.1 + broadSupport * 4.4;
    const center = totalGrip ? supportX / totalGrip : 0;
    const supportLine = supports === 2 ? diagonal / 2 : 0;
    this.rollSpeed += (-lateral * 7.5 + 7 * Math.sin(this.roll)
      - rollRestore * this.roll - damping * this.rollSpeed + supportLine * this.pitch * 7) * dt;
    this.pitchSpeed += (center * 1.3 - overLift * 10 + .022 * acceleration
      + 2.6 * Math.sin(this.pitch) - pitchRestore * this.pitch - damping * this.pitchSpeed) * dt;
    this.roll += this.rollSpeed * dt; this.pitch += this.pitchSpeed * dt;
    this.airTime = supports === 0 ? this.airTime + dt : Math.max(0, this.airTime - dt * 2);
    this.height += ((1.26 - this.airTime * 1.4 - (supports === 1 ? .15 : 0)) - this.height) * (1 - Math.exp(-dt * 12));
    if (Math.abs(this.roll) > BALANCE_LIMITS.roll || Math.abs(this.pitch) > BALANCE_LIMITS.pitch || this.airTime > .42) {
      this.phase = 'fallen'; this.falls++; this.fallAge = 0;
      this.reason = this.airTime > .42 ? '네 발이 모두 떠서 지지를 잃었어.' : Math.abs(this.roll) > BALANCE_LIMITS.roll ? '발을 내리는 타이밍이 어긋나 옆으로 기울었어.' : '앞뒤 지지가 무너졌어.';
      return;
    }
    if (this.x >= COURSE_LENGTH) { this.x = COURSE_LENGTH; this.phase = 'won'; this.speed = 0; }
  }
  /** Qualify real lift/return strokes, never award motion for a key event alone. */
  private coordinateStroke(dt: number, input: readonly boolean[]) {
    let mask = 0;
    for (let i = 0; i < 4; i++) if (input[i]) mask |= 1 << i;
    // Invalid support combinations immediately stop earning propulsion. Pose and
    // balance still respond normally, including while a stroke is disqualified.
    let pair = 0;
    for (const diagonal of DIAGONALS) {
      if (mask !== 0 && (mask & ~diagonal) === 0) { pair = diagonal; break; }
    }
    if (mask !== 0 && !pair) { this.clearStrokes(); return; }
    if (this.time - this.previousRelease > STROKE_TIMING.maxGap) {
      this.previousPair = 0; this.drivingPair = 0;
    }
    // A released pair has only its finite remaining return travel to spend.
    if (this.drivingPair & mask) this.drivingPair = 0;
    if (this.strokePair && !(mask & this.strokePair)) {
      if (this.coherentLift + 1e-9 >= STROKE_TIMING.minLift
          && this.strokeAge <= STROKE_TIMING.maxLift + 1e-9) {
        this.drivingPair = this.previousPair && this.previousPair !== this.strokePair ? this.strokePair : 0;
        this.previousPair = this.strokePair;
        this.previousRelease = this.time;
      } else { this.previousPair = 0; this.drivingPair = 0; }
      this.strokePair = 0;
    }
    if (!(mask & this.exhaustedPair)) this.exhaustedPair = 0;
    if (pair && !this.strokePair && pair !== this.exhaustedPair) {
      this.strokePair = pair; this.strokeAge = this.coherentLift = this.chordAge = 0;
    }
    if (this.strokePair) {
      this.strokeAge += dt;
      if (mask === this.strokePair) { this.coherentLift += dt; this.chordAge = 0; }
      else this.chordAge += dt;
      if (this.chordAge > STROKE_TIMING.chordGrace + 1e-9
          || this.strokeAge > STROKE_TIMING.maxLift + 1e-9) {
        this.exhaustedPair = this.strokePair;
        this.clearStrokes();
      }
    }
  }
  private clearStrokes() {
    this.strokePair = this.previousPair = this.drivingPair = 0;
    this.strokeAge = this.coherentLift = this.chordAge = 0;
    this.previousRelease = -Infinity;
  }
  private moveLeg(leg: Leg, held: boolean, dt: number) {
    leg.lift += ((held ? 1 : 0) - leg.lift) * (1 - Math.exp(-dt * 10));
    leg.reach = held ? Math.min(.55, leg.reach + dt * 5.6) : Math.max(-.10, leg.reach - dt * 6);
  }
  stand() {
    if (this.phase !== 'fallen' || this.fallAge < .4) return false;
    this.clearStrokes(); this.exhaustedPair = 0;
    this.phase = 'running'; this.height = 1.26; this.speed = 0; this.fallAge = 0;
    this.roll = this.pitch = this.rollSpeed = this.pitchSpeed = this.airTime = 0;
    this.legs.forEach((l, i) => { l.reach = -.10; l.lift = 0; l.contact = true; l.plantX = this.x + LEGS[i].x - .10; });
    return true;
  }
  snapshot() {
    return { phase: this.phase, x: this.x, speed: this.speed, time: this.time, falls: this.falls, steps: this.steps,
      roll: this.roll, pitch: this.pitch, balanceRisk: this.balanceRisk, height: this.height, legs: this.legs.map(l => ({ ...l })) };
  }
}
export function demoInput(time: number): boolean[] {
  const pair = Math.floor(time / .12) % 2 === 0;
  return [pair, !pair, !pair, pair];
}
